import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/api/endpoints';

/**
 * Shortcut route "abrir chat com um profissional".
 *
 * URL: /dashboard/mensagens/novo?therapistId=X
 *
 * Descobre o clientProfile.id do usuário logado e redireciona pra thread.
 * Se o usuário não tem ClientProfile ainda (usuário novo ou cadastrado
 * como profissional), redireciona pra onboarding preservando o alvo
 * pra ele voltar depois pro chat.
 *
 * Usado pelo botão "Conversar" na lista e no perfil do profissional —
 * permite montar um link seguro sem descobrir o clientId no componente
 * público (que nem sempre tem sessão).
 */
export const Route = createFileRoute('/dashboard/mensagens/novo')({
  validateSearch: (s: Record<string, unknown>) => ({
    therapistId: typeof s.therapistId === 'string' ? s.therapistId : '',
  }),
  // Auth gate herdado do pai /dashboard (dashboard.tsx) que preserva `next`.
  component: NewChatPage,
});

function NewChatPage() {
  const navigate = useNavigate();
  const { therapistId } = useSearch({ from: '/dashboard/mensagens/novo' });

  const { data: me, isLoading, isError } = useQuery({ queryKey: ['me'], queryFn: authApi.me });

  useEffect(() => {
    if (!me || !therapistId) return;
    const clientId = me.clientProfile?.id;
    if (!clientId) {
      // Usuário logado mas sem ClientProfile — completa onboarding e volta.
      const next = `/dashboard/mensagens/novo?therapistId=${encodeURIComponent(therapistId)}`;
      navigate({ to: '/onboarding/client', search: { next } });
      return;
    }
    navigate({
      to: '/dashboard/mensagens/thread',
      search: { clientId, therapistId },
      replace: true,
    });
  }, [me, therapistId, navigate]);

  if (isError) {
    return <div className="p-8 text-sm text-red-600">Falha ao abrir a conversa. Tente novamente.</div>;
  }
  return (
    <div className="p-8 text-sm text-slate-500">
      {isLoading ? 'Carregando…' : 'Abrindo conversa…'}
    </div>
  );
}
